import React, { useState, useEffect } from 'react';
import { getToken, removeToken, removeRefreshToken, removeUser } from '../utils/storage';
import { Link, useLocation, useNavigate } from '../utils/SimpleRouter';
import { LayoutDashboard, Building2, Users, Globe, Settings, History, AlertCircle, LogOut, UserCog, Menu, X, Mail, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminLayout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [currentOrgName, setCurrentOrgName] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    fetchOrganizations();
    checkImpersonation();

    // Listen to language changes and force re-render
    const handleLanguageChanged = () => {
      forceUpdate(prev => prev + 1);
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  const fetchOrganizations = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Fetched organizations:', response.data);
      setOrganizations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      setOrganizations([]);
    }
  };

  const checkImpersonation = () => {
    // Super Admin layout should never show impersonation state
    // Impersonation happens in a separate window
    setIsImpersonating(false);
  };

  const handleImpersonate = async (orgId) => {
    try {
      const token = getToken();
      const response = await axios.post(`${API}/admin/impersonate/${orgId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open in new window/tab instead of replacing current session
      const newWindow = window.open('about:blank', '_blank');
      
      // Write a small script to set token and redirect
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head><title>Laster...</title></head>
            <body>
              <p>Åpner kunde-panel...</p>
              <script>
                // Use sessionStorage for impersonation to avoid affecting other windows
                sessionStorage.setItem('token', '${response.data.access_token}');
                sessionStorage.setItem('user', JSON.stringify({
                  impersonating: true,
                  organization_id: '${orgId}',
                  organization_name: '${response.data.organization_name}'
                }));
                sessionStorage.setItem('isImpersonationWindow', 'true');
                window.location.href = '/dashboard';
              </script>
            </body>
          </html>
        `);
        newWindow.document.close();
        
        toast.success(`Åpner ${response.data.organization_name} i nytt vindu`);
      } else {
        toast.error('Kunne ikke åpne nytt vindu. Sjekk popup-blocker.');
      }
    } catch (error) {
      console.error('Impersonation failed:', error);
      toast.error('Kunne ikke logge inn som kunde');
    }
  };

  const handleStopImpersonation = async () => {
    try {
      const token = getToken();
      const response = await axios.post(`${API}/admin/stop-impersonation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store new token
      localStorage.setItem('token', response.data.access_token);
      
      // Update user info
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      delete user.impersonating;
      delete user.organization_id;
      localStorage.setItem('user', JSON.stringify(user));
      
      setIsImpersonating(false);
      setCurrentOrgName('');
      
      toast.success('Tilbake til Super Admin');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Stop impersonation failed:', error);
      toast.error('Kunne ikke stoppe impersonation');
    }
  };

  const handleLogout = () => {
    removeToken();
    removeRefreshToken();
    removeUser();
    // Clear session storage for language reset on next login
    sessionStorage.removeItem('userLanguageSet');
    toast.success('Logget ut');
    navigate('/login');
  };

  // Navigation items - defined inside component to re-evaluate translations on language change
  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: t('superAdmin.navigation.overview') },
    { path: '/admin/organizations', icon: Building2, label: t('superAdmin.navigation.organizations') },
    { path: '/admin/export', icon: Download, label: t('superAdmin.navigation.exportData') },
    { path: '/admin/users', icon: Users, label: t('superAdmin.navigation.users') },
    { path: '/admin/languages', icon: Globe, label: t('superAdmin.navigation.languages') },
    { path: '/admin/email-templates', icon: Mail, label: t('superAdmin.navigation.emailTemplates') },
    { path: '/admin/settings', icon: Settings, label: t('superAdmin.navigation.systemSettings') },
    { path: '/admin/login-history', icon: History, label: t('superAdmin.navigation.loginHistory') },
    { path: '/admin/service-announcements', icon: AlertCircle, label: t('superAdmin.navigation.serviceAnnouncements') }
  ];

  return (
    <div className="admin-layout">
      {/* Mobile Header with Hamburger */}
      <div className="mobile-header">
        <h2 style={{ color: '#50E3C2', margin: 0, fontSize: '1.25rem' }}>Super Admin</h2>
        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div className={`admin-sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <h2 style={{ color: '#50E3C2' }}>Super Admin</h2>
        <p style={{ color: '#a0a0a0', fontSize: '0.85rem', marginBottom: '1rem' }}>Administrer hele plattformen</p>
        
        {/* Impersonation Section */}
        {isImpersonating ? (
          <div style={{
            background: 'rgba(80, 227, 194, 0.1)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            border: '1px solid rgba(80, 227, 194, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <UserCog size={16} color="#50E3C2" />
              <span style={{ color: '#50E3C2', fontSize: '0.85rem', fontWeight: '600' }}>
                Innlogget som kunde
              </span>
            </div>
            <p style={{ color: '#ffffff', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              {currentOrgName || 'Kunde'}
            </p>
            <button
              onClick={handleStopImpersonation}
              style={{
                background: '#50E3C2',
                color: '#2A2A2A',
                border: 'none',
                padding: '0.5rem',
                borderRadius: '4px',
                width: '100%',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Tilbake til Super Admin
            </button>
          </div>
        ) : (
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '8px'
          }}>
            <label style={{ 
              color: '#a0a0a0', 
              fontSize: '0.85rem', 
              display: 'block',
              marginBottom: '0.5rem'
            }}>
              <UserCog size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              {t('superAdmin.loginAsCustomer')}
            </label>
            <select
              key={organizations.length}
              onChange={(e) => {
                const orgId = e.target.value;
                if (orgId) {
                  handleImpersonate(orgId);
                  // Reset select after impersonation
                  e.target.value = "";
                }
              }}
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(0,0,0,0.3)',
                color: '#ffffff',
                fontSize: '0.85rem',
                cursor: 'pointer'
              }}
              defaultValue=""
            >
              <option value="" style={{ color: '#ffffff' }}>{t('superAdmin.selectCustomer', { count: organizations.length })}</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id} style={{ color: '#ffffff' }}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
        )}
        
        <nav className="admin-nav">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Icon size={20} />
                {item.label}
              </Link>
            );
          })}
          <div style={{ marginTop: 'auto', padding: '0 1rem 1rem' }}>
            <LanguageSwitcher />
          </div>
          <button
            onClick={handleLogout}
            style={{
              background: 'none',
              border: 'none',
              color: '#e0e0e0',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              width: '100%',
              textAlign: 'left',
              fontSize: '1rem'
            }}
          >
            <LogOut size={20} />
            {t('superAdmin.navigation.logout')}
          </button>
        </nav>
      </div>
      <div className="admin-main">
        {children}
      </div>
    </div>
  );
};

export default SuperAdminLayout;
